import { Injectable } from '@angular/core';

import { environment } from 'env/environment';

export const CORE_HOST_QUERY_PARAM = 'coreHost';

const HOST_LABEL_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

// Dev envs serve the Nova rest-server on its own host instead of behind Core's `/api/nova` proxy.
const DEV_CORE_HOST_SUFFIX = '.khcore.dev';
const DEV_GAME_SERVER_HOST_SUFFIX = '.gameserver.knowledgehook.dev';
// Only env names the dev game-server gateway routes; any other `*.khcore.dev` keeps Core's proxy.
const DEV_GAME_SERVER_ENV_PATTERN = /^(?:test[1-9]\d?|snapshot|rc|latest)$/;
const GAME_SERVER_PATH = '/game/v1';

@Injectable({
  providedIn: 'root'
})
export class UrlService {
  private static readonly STORAGE_COREAPIURL_KEY: string = 'CoreApiUrl';
  // Read by the Nova games (`@nova/network` `getGameApiUrl`), so they don't re-derive the game server from Core.
  private static readonly STORAGE_GAMEAPIURL_KEY: string = 'GameApiUrl';

  public static resolveCoreUrlFromHost(hostLabel: string, baseCoreUrl = environment.coreApiUrl): string | null {
    if (!HOST_LABEL_PATTERN.test(hostLabel)) {
      return null;
    }

    try {
      const parsed = new URL(baseCoreUrl);
      const hostname = parsed.hostname.toLowerCase();
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return null;
      }

      const dot = hostname.indexOf('.');
      if (dot <= 0) {
        return null;
      }

      parsed.hostname = `${hostLabel}${hostname.slice(dot)}`;
      return parsed.href.replace(/\/+$/, '');
    }
    catch {
      return null;
    }
  }

  /** Maps a dev Core URL (`https://{env}.khcore.dev`) to that env's game server, or null for any other Core URL. */
  public static resolveDevGameServerUrl(coreUrl: string): string | null {
    try {
      const hostname = new URL(coreUrl).hostname.toLowerCase();
      if (!hostname.endsWith(DEV_CORE_HOST_SUFFIX)) {
        return null;
      }

      const env = hostname.slice(0, -DEV_CORE_HOST_SUFFIX.length);
      if (!DEV_GAME_SERVER_ENV_PATTERN.test(env)) {
        return null;
      }

      return `https://${env}${DEV_GAME_SERVER_HOST_SUFFIX}${GAME_SERVER_PATH}`;
    }
    catch {
      return null;
    }
  }

  public constructor() {
    this.applyCoreHostFromSearch(window.location.search);
    this.storeGameServerApiUrl();
  }

  public applyCoreHostFromSearch(search: string): void {
    const coreHost = new URLSearchParams(search).get(CORE_HOST_QUERY_PARAM);
    if (!coreHost) {
      return;
    }

    const coreUrl = UrlService.resolveCoreUrlFromHost(coreHost);
    if (coreUrl) {
      this.setServerApiUrl(coreUrl);
    }
  }

  public setServerApiUrl(url: string): void {
    if (url) {
      url = url.replace(/\/+$/, '');
      localStorage.setItem(UrlService.STORAGE_COREAPIURL_KEY, url);
      this.storeGameServerApiUrl();
    }
  }

  public getServerApiUrl(): string {
    return localStorage.getItem(UrlService.STORAGE_COREAPIURL_KEY) ??
      environment.coreApiUrl;
  }

  /** Whether `url` points at the same Core host as the one this session is using. */
  public isCurrentServerApiUrl(url: string): boolean {
    const normalize = (value: string) => {
      try {
        return new URL(value).host.toLowerCase();
      }
      catch {
        return value.replace(/\/+$/, '').toLowerCase();
      }
    };

    return normalize(url) === normalize(this.getServerApiUrl());
  }

  public buildServerApiUrl(...parts: string[]): string {
    return [this.getServerApiUrl()]
      .concat(parts)
      .join('/');
  }

  /** Game server base URL for the current Core: the dev game server host, else Core's `/api/nova` proxy. */
  public getGameServerApiUrl(): string {
    return UrlService.resolveDevGameServerUrl(this.getServerApiUrl()) ?? this.buildServerApiUrl('api/nova');
  }

  public buildGameServerApiUrl(...parts: string[]): string {
    return [this.getGameServerApiBaseUrl()]
      .concat(parts)
      .join('/');
  }

  public buildIdentityServerApiUrl(...parts: string[]): string {
    return this.buildServerApiUrl('Login', ...parts);
  }

  private getGameServerApiBaseUrl(): string {
    return environment.overrideGameApiUrl ?? this.getGameServerApiUrl();
  }

  private storeGameServerApiUrl(): void {
    localStorage.setItem(UrlService.STORAGE_GAMEAPIURL_KEY, this.getGameServerApiBaseUrl());
  }
}
